import React, { useState, useRef, useCallback, useEffect } from 'react'
import { observer } from "mobx-react"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { PendingSignatureItemType } from 'modules/main/models/order-content-task-signatures'
import { normalizeSignatureBase64, useCryptoPro, SignatureVerification } from './useCryptoPro'
import styles from "./CryptoDialog.module.scss"
import { PlanRDndUploader } from 'modules/common/components/planr/dndUploader/FileDndUploader'

/** Из DN сертификата ("CN=Иванов, O=…") берём только имя подписанта */
const signerDisplayName = (subjectName: string): string => {
  const match = /CN=([^,]+)/.exec(subjectName)
  return match ? match[1].trim() : subjectName
}

export interface AttachSignatureFormState {
  /** Нормализованная подпись (base64 от DER) или null, если файл ещё не выбран */
  sigBase64: string | null
  /** Хеш файла результата совпал с файлом в базе */
  hashMatch: boolean
  /** Имена подписантов из сертификатов внутри .sig (если КриптоПро смог разобрать) */
  signerNames: string[]
}

interface AttachSignatureFormProps {
  selectedSignature: PendingSignatureItemType
  baseUrl: string
  onNotifyError: (error: string) => void
  onNotifySuccess?: (message: string) => void
  /** Вызывается при любом изменении готовности формы — родитель решает, можно ли прикреплять */
  onChange: (state: AttachSignatureFormState) => void
}

/**
 * Форма прикрепления внешнего файла подписи (.sig): файл результата от подписанта
 * для сверки хеша с файлом в базе, сам .sig и проверка подписи через КриптоПро.
 * Кнопку «Прикрепить» рисует родитель — форма только сообщает готовность через onChange.
 */
export const AttachSignatureForm: React.FC<AttachSignatureFormProps> = observer(({
  selectedSignature,
  baseUrl,
  onNotifyError,
  onNotifySuccess,
  onChange,
}) => {
  const { verifyFileHash, verifySignature } = useCryptoPro()
  const [selectedSigFile, setSelectedSigFile] = useState<File | null>(null)
  const [sigBase64, setSigBase64] = useState<string | null>(null)
  const [selectedResultFile, setSelectedResultFile] = useState<File | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [hashMatch, setHashMatch] = useState(false)
  // Проверка самой подписи против файла результата: не блокирует прикрепление,
  // но предупреждает, если КриптоПро её не подтверждает (подпись под другой файл,
  // недоверенная цепочка и т.п.) — после слияния с подписями сотрудников такой
  // контейнер перестал бы проверяться целиком
  const [isCheckingSig, setIsCheckingSig] = useState(false)
  const [sigCheck, setSigCheck] = useState<SignatureVerification | null>(null)

  const resultFileInputRef = useRef<HTMLInputElement>(null)
  const sigFileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const signerNames = sigCheck ? sigCheck.signatures.map((info) => signerDisplayName(info.subjectName)) : []
    onChange({ sigBase64, hashMatch, signerNames })
  }, [sigBase64, hashMatch, sigCheck, onChange])

  const checkSignatureAgainstFile = useCallback(async (signature: string | null, file: File | null) => {
    setSigCheck(null)

    if (!signature || !file) {
      return
    }

    try {
      setIsCheckingSig(true)
      const result = await verifySignature(signature, await file.arrayBuffer())
      setSigCheck(result)
    } catch (error) {
      setSigCheck({ signatures: [], verified: false, error: (error as any).message })
    } finally {
      setIsCheckingSig(false)
    }
  }, [verifySignature])

  const handleResultFileSelect = async (file: File) => {
    setSelectedResultFile(file)
    setVerificationStatus('idle')
    setHashMatch(false)
    checkSignatureAgainstFile(sigBase64, file)

    try {
      setIsVerifying(true)

      const isValid = await verifyFileHash(
        file,
        selectedSignature.fileId,
        selectedSignature.fileName,
        baseUrl
      )

      if (isValid) {
        setVerificationStatus('success')
        setHashMatch(true)
        onNotifySuccess?.('Хэш файла совпадает с файлом в базе. Можно прикреплять подпись.')
      } else {
        setVerificationStatus('error')
        setHashMatch(false)
        onNotifyError('Хэш загруженного файла НЕ совпадает с файлом в базе! Подпись не будет принята.')
      }
    } catch (error) {
      setVerificationStatus('error')
      onNotifyError(`Ошибка верификации: ${(error as any).message}`)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSigFileSelect = async (file: File) => {
    if (!file) {
      return
    }

    const resetInput = () => {
      setSelectedSigFile(null)
      setSigBase64(null)
      setSigCheck(null)
      if (sigFileInputRef.current) {
        sigFileInputRef.current.value = ''
      }
    }

    if (!file.name.endsWith('.sig')) {
      onNotifyError('Пожалуйста, выберите файл с расширением .sig')
      resetInput()
      return
    }

    try {
      // Файл подписи может быть как в DER, так и в кодировке BASE64 (её ставит
      // по умолчанию КриптоАРМ) — приводим к единому виду, иначе подпись
      // сохранится нечитаемой для плагина КриптоПро
      const buffer = await file.arrayBuffer()
      const bytes = new Uint8Array(buffer)

      let binary = ''
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }

      const normalized = normalizeSignatureBase64(btoa(binary))
      setSigBase64(normalized)
      setSelectedSigFile(file)
      checkSignatureAgainstFile(normalized, selectedResultFile)
    } catch (error) {
      onNotifyError((error as any).message)
      resetInput()
    }
  }

  return (
    <>
      {/* Файл результата для сверки хеша */}
      <div className={styles.fileSection}>
        <div className={styles.fileSectionTitle}>
          <GeneralIcon type="general-document" style={{ marginRight: '8px' }} />
          Файл результата (от подписанта) <span style={{ color: '#e31818' }}>*</span>
        </div>
        <div
          className={styles.fileInputArea}
          onClick={() => resultFileInputRef.current?.click()}
        >
          <PlanRDndUploader
            accept="*"
            multiple={false}
            withLoader={true}
            style={{
              border: "2px dashed #ea561e",
              height: "50px",
              width: "100%",
              maxWidth: "100%",
            }}
            onFileSelected={handleResultFileSelect}
          />
        </div>
        {selectedResultFile && (
          <div className={styles.selectedFileInfo}>
            <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
            <span>{selectedResultFile.name}</span>
            <PlanrButton
              type="neutral"
              size="small"
              onClick={() => {
                setSelectedResultFile(null)
                setVerificationStatus('idle')
                setHashMatch(false)
                setSigCheck(null)
                if (resultFileInputRef.current) resultFileInputRef.current.value = ''
              }}
              style={{ marginLeft: 'auto' }}
            >
              Очистить
            </PlanrButton>
          </div>
        )}
        {isVerifying && (
          <div className={styles.verifyingInfo}>
            <LocalLoader active={true} />
            <span style={{ marginLeft: '8px' }}>Проверка хэша файла...</span>
          </div>
        )}
        {verificationStatus === 'success' && (
          <div className={styles.verifySuccess}>
            <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
            <span>Хэш совпадает. Файл соответствует файлу в базе данных.</span>
          </div>
        )}
        {verificationStatus === 'error' && (
          <div className={styles.verifyError}>
            <GeneralIcon type="general-cross" style={{ color: "#e31818", marginRight: "8px" }} />
            <span>Хэш НЕ совпадает! Файл был изменен. Подпись недействительна.</span>
          </div>
        )}
      </div>

      {/* Файл подписи */}
      <div className={styles.fileSection}>
        <div className={styles.fileSectionTitle}>
          <GeneralIcon type="general-lock" style={{ marginRight: '8px' }} />
          Файл открепленной подписи (.sig) <span style={{ color: '#e31818' }}>*</span>
        </div>
        <div
          className={styles.fileInputArea}
          onClick={() => sigFileInputRef.current?.click()}
        >
          <PlanRDndUploader
            accept={"*"}
            multiple={false}
            withLoader={true}
            style={{
              border: "2px dashed #ea561e",
              height: "50px",
              maxWidth: "100%",
              width: "100%",
            }}
            onFileSelected={handleSigFileSelect}
          />
        </div>
        {selectedSigFile && (
          <div className={styles.selectedFileInfo}>
            <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
            <span>{selectedSigFile.name}</span>
            <PlanrButton
              type="neutral"
              size="small"
              onClick={() => {
                setSelectedSigFile(null)
                setSigBase64(null)
                setSigCheck(null)
                if (sigFileInputRef.current) sigFileInputRef.current.value = ''
              }}
              style={{ marginLeft: 'auto' }}
            >
              Очистить
            </PlanrButton>
          </div>
        )}
        {isCheckingSig && (
          <div className={styles.verifyingInfo}>
            <LocalLoader active={true} />
            <span style={{ marginLeft: '8px' }}>Проверка подписи по файлу результата...</span>
          </div>
        )}
        {sigCheck && sigCheck.verified && (
          <div className={styles.verifySuccess}>
            <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
            <span>
              Подпись подтверждена КриптоПро
              {sigCheck.signatures.length > 0 && (
                <>: {sigCheck.signatures.map((info) => signerDisplayName(info.subjectName)).join(', ')}</>
              )}
            </span>
          </div>
        )}
        {sigCheck && !sigCheck.verified && (
          <div className={styles.verifyError}>
            <GeneralIcon type="general-warning" style={{ color: "#fbb360", marginRight: "8px" }} />
            <span>
              КриптоПро не подтвердил подпись по этому файлу{sigCheck.error ? `: ${sigCheck.error}` : ''}.
              Прикрепить всё равно можно, но проверка ЭЦП у такого файла будет выдавать ошибку.
            </span>
          </div>
        )}
      </div>
    </>
  )
})
